<template>
    <brick-square-base class="journey-brick" :class="classList" @dblclick.native="onDoubleClick">
        <template>
            <touchpoint-overview-popup
                v-if="showOverview"
                @hide="closeOverview()"

                :brick="brick"
            />
            
            <div class="brick-menu">
                <has-unseen-comments-marker class="has-unseen-comments-marker" v-if="hasUnseenComments" />
                
                <brick-dropdown-menu
                        :has-connections="hasConnections"
                        :brick-id="id"
                        :is-child-journey-brick="isChildJourneyBrick"
                        :configured-journey-id="configuredJourneyId"
                        :is-square-brick="true"
                        
                        @info-click="openOverview()"
                        @add-connection-click="$emit('add-connection', $event)"
                        @delete-click="$emit('delete-brick', true)"
                        @settings-click="showSettings = true"
                />
            </div>

            <div class="brick-icon">
                <brick-icon :brick-type="brick.type" :brick-config="brick.config" />
            </div>

            <div class="brick-title">
                <span class="center">
                    {{name}}
                </span>
            </div>

            <component v-if="showSettings"
                       :is="editorType"

                       :brick="brick"
                       :show="showSettings"
                       @hidden="onSettingsHidden()"
                       @save="onSave($event)"
                       @delete="onDelete()"
            />

        </template>
    </brick-square-base>
</template>

<script>
    import {createNamespacedHelpers} from 'vuex';

    import snowplow from '@modules/JourneyBuilder/snowplow';
    import brickTypes from "@modules/JourneyBuilder/Bricks/brick-types";
    import api from "@modules/JourneyBuilder/api";

    import journeyBrickMixin from '@modules/JourneyBuilder/Mixins/journey-brick'

    import BrickSquareBase from '@modules/JourneyBuilder/Components/Patterns/BrickSquareBase.vue';
    import BrickDropdownMenu from './BrickDropdownMenu';
    import BrickIcon from "./BrickIcon";
    import {TouchpointOverviewPopup} from './TouchpointOverview';
    import {HasUnseenCommentsMarker} from "./Comments";

    import {
        AudienceSettings,
        TouchPointSettings,
        JourneySettings,
        GoalSettings
    } from "./Settings";
    
    const {mapGetters} = createNamespacedHelpers('journeyBuilder/composer');
    
    const brickTypeEditorMapping = {
        [brickTypes.audience]: AudienceSettings,
        [brickTypes.touchpoint]: TouchPointSettings,
        [brickTypes.journey]: JourneySettings,
        [brickTypes.goal]: GoalSettings 
    };
    
    export default {
        name: 'brick-square',
        components: {
            HasUnseenCommentsMarker,
            BrickSquareBase,
            BrickDropdownMenu,
            TouchpointOverviewPopup,
            BrickIcon
        },
        mixins: [journeyBrickMixin],

        props: {
            /**
             * @type {JourneyBrick}
             */
            brick: {
                type: Object,
                required: true
            },
            isHighlighted: {
                type: Boolean,
                required: false,
                default: false
            },
            IncomingConnectionsInfo: {
                type: Array,
                required: true
            },
            OutgoingConnectionsInfo: {
                type: Array,
                required: true
            }
        },
        
        computed: {
            ...mapGetters(['menuShown']),
            ...mapGetters({
                'storeHasUnseenComments': 'hasUnseenComments'
            }),
            hasUnseenComments(){
                return this.storeHasUnseenComments(this.id);
            },
            hasConnections() {
                return this.IncomingConnectionsInfo.length !== 0 || this.OutgoingConnectionsInfo.length !== 0;
            },
            editorType(){
                return brickTypeEditorMapping[this.type];
            },
            configuredJourneyId(){
                if(this.type === brickTypes.journey){
                    return this.config.journeyId || this.config.parentJourneyId;
                }
                
                return null;
            },
            classList() {
                const vm = this;
                
                const list = [];

                list.push(vm.config.goal === true ? 'goal' : vm.brickTypeName);
                list.push(vm.isActive ? 'active' : 'inactive');

                if(vm.isChildJourneyBrick){
                    list.push('child-journey');
                }
                
                if(vm.isHighlighted){
                    list.push('highlighted');
                }
                
                if(vm.menuShown(vm.id)){
                    list.push('menu-dropdown-shown');
                }
                
                return list;
            },
            
            id(){ 
                return this.brick.id;
            },
            name(){
                return this.brick.name;
            },
            isChildJourneyBrick(){
                return this.brick.isChildJourneyBrick;
            }
        },
        
        data() {
            return {
                settings: {},
                showSettings: false,
                showOverview: false
            }
        },

        methods: {
            openOverview(){
                snowplow.trackStructuredEvent('show-brick-overview');
                this.showOverview = true;
            },
            
            closeOverview(){
                this.showOverview = false;
            },
            
            onDoubleClick(){
                if(this.type === brickTypes.journey) {
                    this.navigateToJourneyBrick();
                }
            },
            
            navigateToJourneyBrick(){
                const journeyId = this.configuredJourneyId;

                if(journeyId){
                    window.open(api.getViewJourneyUrl(journeyId), '_blank');
                }
            },
            
            onSettingsHidden(){
                this.showSettings = false;
            },
            
            onSave(evt){
                this.showSettings = false;
                this.$emit('settings-changed', evt);
            },
            
            onDelete(){
                const REQUIRES_PROMPT = false;
                
                this.showSettings = false;
                this.$emit('delete-brick', REQUIRES_PROMPT);
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "~@modules/JourneyBuilder/Style/journey-brick-square.scss";
</style>